import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * MarketingUpgradeCard — one-click upsell into the $35/mo Marketing add-on.
 *
 * The Marketing add-on unlocks: shop builder, live streaming, food-truck /
 * pop-up GPS check-ins, and promo codes — for every owner type (business,
 * verified influencer, approved band).
 *
 * Props:
 *   • compact  — smaller variant used inside gate banners
 *   • active   — hide the card entirely when the caller already has the add-on
 *   • note     — optional extra copy under the perks list
 */
export default function MarketingUpgradeCard({ compact = false, active = false, note = "" }) {
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/subscription/marketing/checkout", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  if (active) {
    return (
      <Card className={`rounded-md p-4 bg-emerald-50 border-emerald-200 flex items-center gap-3 ${compact ? "" : "mt-4"}`} data-testid="marketing-active-badge">
        <Check size={18} className="text-emerald-700" />
        <div className="text-sm text-emerald-900 font-semibold">Marketing add-on active — shop, live, check-ins, promos unlocked.</div>
      </Card>
    );
  }

  if (compact) {
    return (
      <Button
        onClick={start}
        disabled={busy}
        className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white"
        data-testid="marketing-upgrade-cta-compact"
      >
        {busy ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Sparkles size={13} className="mr-1.5" />}
        Upgrade — $35/mo
      </Button>
    );
  }

  return (
    <Card className="rounded-md p-6 border-2 border-[#B31E1E]/30 bg-gradient-to-br from-red-50 to-white" data-testid="marketing-upgrade-card">
      <div className="inline-flex items-center gap-1.5 rounded-full bg-[#F9E4E1] text-[#B31E1E] border border-[#F0C9C4] px-3 py-1 text-[11px] font-semibold tracking-wide">
        <Sparkles size={11} /> Marketing add-on
      </div>
      <h3 className="mt-3 font-display text-2xl tracking-tight">Unlock every selling tool for $35/mo</h3>
      <ul className="mt-3 space-y-1.5 text-sm text-[#3E2A26]">
        {[
          "Full product shop — sell physical items with cart checkout",
          "TikTok-Shop-style live streaming (Mux)",
          "Food truck & pop-up GPS check-ins on the live map",
          "Boost your pin to the top of the Food Trucks map",
          "Limited-time promo codes tied to check-ins",
          "Promotional email + in-app inbox broadcasts",
        ].map((p) => (
          <li key={p} className="flex items-start gap-2">
            <Check size={14} className="text-emerald-700 mt-0.5 shrink-0" />
            <span>{p}</span>
          </li>
        ))}
      </ul>
      {note && <p className="mt-3 text-xs text-gray-600">{note}</p>}
      <Button
        onClick={start}
        disabled={busy}
        className="mt-4 rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white shadow-sm"
        data-testid="marketing-upgrade-cta"
      >
        {busy ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Redirecting…</> : <><Sparkles size={14} className="mr-1.5" /> Subscribe — $35/mo</>}
      </Button>
    </Card>
  );
}
